import { createElement, Fragment, type ReactNode } from "react";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { keys } from "@/lib/i18n/portal";

type Translate = (key: string, values?: Record<string, string | number>) => string;

/**
 * The admin-facing message for a failed tier mutation (activate, cancel,
 * grant, revoke). A stale revision, a re-priced plan and a short wallet are
 * different situations with different next steps, and a 5xx/network failure
 * must not read like a conflict the admin can fix by retrying the same thing.
 *
 * `shortfall` is what the latest quote says the wallet still lacks; the 409
 * itself carries no amount.
 *
 * Any other 4xx quotes the server's message, which is English. Interpolated
 * into the Arabic sentence as plain text, its Latin run and the Arabic label
 * shared one bidi paragraph and the punctuation landed on the wrong side, so
 * the quote is returned inside its own `<bdi>` (direction from its content).
 */
export function describeTierError(
  error: unknown,
  t: Translate,
  formatShortfall: (() => string | null) | null = null,
): ReactNode {
  if (!(error instanceof ApiJsonClientError) || error.status >= 500)
    return t(keys.admin.tierErrorFailed);
  switch (error.code) {
    case "SUBSCRIPTION_CHANGED":
      return t(keys.admin.tierErrorChanged);
    case "SUBSCRIPTION_PLAN_CHANGED":
      return t(keys.admin.tierErrorPlanChanged);
    case "SUBSCRIPTION_WALLET_INSUFFICIENT": {
      const amount = formatShortfall?.() ?? null;
      return amount
        ? t(keys.admin.tierErrorWalletShort, { amount })
        : t(keys.admin.tierErrorWalletInsufficient);
    }
    case "SUBSCRIPTION_COMPLIMENTARY_NOT_ACTIVE":
      return t(keys.admin.tierErrorComplimentaryNotActive);
    case "SUBSCRIPTION_COMPLIMENTARY_ALREADY_ACTIVE":
      return t(keys.admin.tierErrorComplimentaryActive);
    case "SUBSCRIPTION_PAID_PERIOD_ACTIVE":
      return t(keys.admin.tierErrorPaidPeriodActive);
    default:
      return error.status >= 400 && error.message
        ? withIsolatedMessage(t(keys.admin.tierErrorRejected), error.message)
        : t(keys.admin.tierErrorFailed);
  }
}

/** The template around `{message}`, with the server's text in a `<bdi>`. */
function withIsolatedMessage(template: string, message: string): ReactNode {
  const [before, after = ""] = template.split("{message}");
  return createElement(
    Fragment,
    null,
    before,
    createElement("bdi", null, message),
    after,
  );
}
